import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Check, X } from "@phosphor-icons/react";

/**
 * Medidor de força de senha em tempo real.
 * Score 0–4 baseado em: comprimento, letras, números, símbolos.
 */
function scorePassword(pw) {
  if (!pw) return { score: 0, checks: {} };
  const checks = {
    length: pw.length >= 8,
    letter: /[a-zA-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    special: /[^a-zA-Z0-9]/.test(pw),
  };
  const score = Object.values(checks).filter(Boolean).length;
  return { score, checks };
}

const LABELS = ["fraquíssima", "fraca", "razoável", "forte", "excelente"];
const COLORS = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#10b981"];

export default function PasswordStrengthMeter({ password }) {
  const { score, checks } = useMemo(() => scorePassword(password || ""), [password]);
  const pct = (score / 4) * 100;
  const color = COLORS[score];
  const label = LABELS[score];

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2" data-testid="pw-strength">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-white/50">Força da senha</span>
        <span className="font-mono font-semibold" style={{ color }}>
          {label}
        </span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%`, backgroundColor: color }}
          transition={{ duration: 0.25 }}
        />
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] mt-1">
        <Rule ok={checks.length} label="8+ caracteres" />
        <Rule ok={checks.letter} label="1 letra" />
        <Rule ok={checks.number} label="1 número" />
        <Rule ok={checks.special} label="1 símbolo" />
      </div>
    </div>
  );
}

function Rule({ ok, label }) {
  return (
    <div className={`flex items-center gap-1.5 ${ok ? "text-[var(--success)]" : "text-white/40"}`}>
      {ok ? <Check size={11} weight="bold" /> : <X size={11} weight="bold" />}
      <span>{label}</span>
    </div>
  );
}
